import { motion } from "framer-motion";
import { Clock, Users, CreditCard, CircleDot } from "lucide-react";

const bowlingPrices = [
  {
    label: "Vor 18:00 Uhr",
    price: "24",
    unit: "€ / Bahn / Std.",
    highlight: false,
  },
  {
    label: "Nach 18:00 Uhr",
    price: "28",
    unit: "€ / Bahn / Std.",
    highlight: true,
  },
];

const extras = [
  { icon: Users, text: "Max. 8 Personen pro Bahn" },
  { icon: CircleDot, text: "Leihschuhe: 2,00 € pro Person" },
  { icon: CreditCard, text: "EC-Zahlung ab 30 € möglich" },
];


export default function Pricing() {
  return (
    <section
      id="pricing"
      className="relative py-28 md:py-36 px-6 md:px-12 lg:px-20 bg-navy"
      data-testid="pricing-section"
    >
      {/* Ambient */}
      <div className="hidden 2xl:block absolute top-0 right-0 w-96 h-96 bg-gold/8 blur-[140px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="skull-divider mb-6">
            <span className="font-space-grotesk text-xs text-gold tracking-[0.3em] uppercase px-3">Preise</span>
          </div>
          <h2 className="font-unbounded text-4xl sm:text-5xl lg:text-6xl font-black text-cream leading-tight">
            Faire Preise,{" "}
            <span className="gold-gradient-text">großer Spaß</span>
          </h2>
          <p className="font-space-grotesk text-cream-muted text-sm md:text-base mt-5 max-w-lg mx-auto text-center leading-relaxed">
            Ab 24 € pro Bahn. In großen Gruppen schon ab 8 € pro Person.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Bowling Prices */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="glass border border-white/5 rounded-2xl p-8 gold-glow-hover"
            data-testid="pricing-bowling"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-gold" />
              </div>
              <div>
                <h3 className="font-unbounded text-xl font-bold text-cream">Bowling</h3>
                <p className="font-space-grotesk text-xs text-cream/50 uppercase tracking-widest">Do – So</p>
              </div>
            </div>

            {/* Price Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {bowlingPrices.map((item) => (
                <div
                  key={item.label}
                  className={`rounded-xl p-6 text-center border ${
                    item.highlight
                      ? "bg-gold/10 border-gold/30"
                      : "bg-white/[0.03] border-white/5"
                  }`}
                >
                  <p className="font-space-grotesk text-xs text-cream/60 uppercase tracking-widest mb-3">
                    {item.label}
                  </p>
                  <div className="flex flex-col items-center gap-1">
                    <div className="flex items-baseline gap-1">
                      <span className="font-unbounded text-4xl font-black gold-gradient-text">
                        {item.price}
                      </span>
                      <span className="font-space-grotesk text-sm text-cream/60">€</span>
                    </div>
                    <span className="font-space-grotesk text-xs text-cream/45">
                      pro Bahn / Std.
                    </span>
                  </div>
                  {item.highlight && (
                    <span className="inline-block mt-3 font-space-grotesk text-xs bg-gold/20 text-gold px-3 py-1 rounded-full border border-gold/30">
                      Abend-Tarif
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Extras */}
            <div className="flex flex-wrap gap-4">
              {extras.map((item) => (
                <div key={item.text} className="flex items-center gap-2 text-cream/70">
                  <item.icon className="w-4 h-4 text-gold/60 flex-shrink-0" />
                  <span className="font-inter text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Billard */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="glass border border-white/5 rounded-2xl p-8 gold-glow-hover"
            data-testid="pricing-billard"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center">
                <CircleDot className="w-5 h-5 text-gold" />
              </div>
              <div>
                <h3 className="font-unbounded text-xl font-bold text-cream">Billard</h3>
                <p className="font-space-grotesk text-xs text-cream/50 uppercase tracking-widest">Ab 12 Jahren</p>
              </div>
            </div>

            <div className="rounded-xl p-6 text-center bg-white/[0.03] border border-white/5">
              <p className="font-space-grotesk text-xs text-cream/60 uppercase tracking-widest mb-3">
                Pro Tisch
              </p>
              <div className="flex items-baseline justify-center gap-1">
                <span className="font-unbounded text-4xl font-black gold-gradient-text">10</span>
                <span className="font-space-grotesk text-sm text-cream/60">€ / Stunde</span>
              </div>
            </div>

            <p className="font-inter text-sm text-cream/50 mt-6">
              Keine Reservierung nötig – wer zuerst kommt, spielt zuerst.
            </p>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-12"
        >
          <a
            href="https://4bowl.de/pirate-bowling/online/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold px-10 py-4 rounded-full text-sm inline-block"
            data-testid="pricing-cta"
          >
            Jetzt Bahn reservieren
          </a>
          <p className="font-inter text-xs text-cream/40 mt-4">
            Sonderkonditionen für Gruppen und Firmenfeiern auf Anfrage.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
